import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Slider,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { usersApi } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useLanguage, Language } from '../i18n';

interface Preferences {
  default_hours: number;
  map_center_lat: string;
  map_center_lng: string;
  map_zoom: number;
}

const Settings: React.FC = () => {
  const { user } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState<Preferences>({
    default_hours: 24,
    map_center_lat: '56.9496',
    map_center_lng: '24.1052',
    map_zoom: 12,
  });

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await usersApi.getPreferences();
      setPreferences(response.data);
    } catch (err) {
      setSnackbar({
        open: true,
        message: t('settings.preferencesLoadFailed'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await usersApi.updatePreferences(preferences);
      setSnackbar({
        open: true,
        message: t('settings.preferencesSaved'),
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: t('settings.preferencesFailed'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (newPassword !== confirmPassword) {
      setPasswordError(t('settings.passwordMismatch'));
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError(t('settings.passwordTooShort'));
      return;
    }

    setChangingPassword(true);
    try {
      await usersApi.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSnackbar({
        open: true,
        message: t('settings.passwordChanged'),
        severity: 'success',
      });
    } catch (err: any) {
      setPasswordError(
        err.response?.data?.detail || t('settings.passwordChangeFailed')
      );
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLanguageChange = (
    _: React.MouseEvent<HTMLElement>,
    newLang: Language | null
  ) => {
    if (newLang !== null) {
      setLanguage(newLang);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        {t('settings.title')}
      </Typography>

      {/* Language Setting */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('settings.language')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('settings.languageLabel')}
          </Typography>
          <ToggleButtonGroup
            value={language}
            exclusive
            onChange={handleLanguageChange}
            size="small"
          >
            <ToggleButton value="lv">
              Latviešu
            </ToggleButton>
            <ToggleButton value="en">
              English
            </ToggleButton>
          </ToggleButtonGroup>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('settings.accountInfo')}
          </Typography>
          <Typography variant="body1">
            <strong>{t('login.email')}:</strong> {user?.email}
          </Typography>
          <Typography variant="body1">
            <strong>{t('admin.role')}:</strong> {user?.role}
          </Typography>
        </CardContent>
      </Card>

      {/* Map Preferences */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('settings.mapSettings')}
          </Typography>

          <Typography gutterBottom>
            {t('settings.defaultHours')}: {preferences.default_hours}
          </Typography>
          <Slider
            value={preferences.default_hours}
            onChange={(_, value) =>
              setPreferences({ ...preferences, default_hours: value as number })
            }
            min={1}
            max={48}
            marks={[
              { value: 1, label: '1h' },
              { value: 24, label: '24h' },
              { value: 48, label: '48h' },
            ]}
            sx={{ mb: 3 }}
          />

          <Button
            variant="contained"
            onClick={handleSavePreferences}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : t('settings.savePreferences')}
          </Button>
        </CardContent>
      </Card>

      {/* Password Change */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('settings.changePassword')}
          </Typography>

          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}

          <form onSubmit={handlePasswordChange}>
            <TextField
              fullWidth
              label={t('settings.currentPassword')}
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label={t('settings.newPassword')}
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              required
              helperText={t('settings.passwordHint')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label={t('settings.confirmPassword')}
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              error={confirmPassword !== '' && newPassword !== confirmPassword}
              helperText={
                confirmPassword !== '' && newPassword !== confirmPassword
                  ? t('settings.passwordMismatch')
                  : ''
              }
            />
            <Button
              type="submit"
              variant="contained"
              disabled={changingPassword}
              sx={{ mt: 2 }}
            >
              {changingPassword ? (
                <CircularProgress size={24} />
              ) : (
                t('settings.changePassword')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
